import { useEffect, useRef, useState, useCallback } from 'react';

interface Props {
  deliveryLat: number | null;
  deliveryLng: number | null;
  customerLat: number | null;
  customerLng: number | null;
  deliveryBoyName?: string;
  customerAddress?: string;
  isLive?: boolean;
  lastUpdated?: Date | null;
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function calcDistance(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function calcBearing(lat1: number, lng1: number, lat2: number, lng2: number) {
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos((lat2 * Math.PI) / 180);
  const x =
    Math.cos((lat1 * Math.PI) / 180) * Math.sin((lat2 * Math.PI) / 180) -
    Math.sin((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function toXY(
  lat: number,
  lng: number,
  bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number },
  W: number,
  H: number,
  pad: number
) {
  const lr = bounds.maxLat - bounds.minLat || 0.01;
  const lgr = bounds.maxLng - bounds.minLng || 0.01;
  return {
    x: pad + ((lng - bounds.minLng) / lgr) * (W - 2 * pad),
    y: pad + ((bounds.maxLat - lat) / lr) * (H - 2 * pad),
  };
}

const W = 360;
const H = 240;
const PAD = 42;

export default function CustomerRouteMap({
  deliveryLat,
  deliveryLng,
  customerLat,
  customerLng,
  deliveryBoyName = 'Delivery Boy',
  customerAddress = '',
  isLive = false,
  lastUpdated = null,
}: Props) {
  const animRef = useRef<number | null>(null);
  const prevBike = useRef<{ x: number; y: number } | null>(null);
  const targetBike = useRef<{ x: number; y: number } | null>(null);
  const curBike = useRef<{ x: number; y: number } | null>(null);
  const animProg = useRef(1);
  const dotAnimRef = useRef(0);

  const [bikePos, setBikePos] = useState<{ x: number; y: number } | null>(null);
  const [custPos, setCustPos] = useState<{ x: number; y: number } | null>(null);
  const [bearing, setBearing] = useState(0);
  const [dotOffset, setDotOffset] = useState(0);
  const [pulsing, setPulsing] = useState(false);

  const distance =
    deliveryLat && deliveryLng && customerLat && customerLng
      ? calcDistance(deliveryLat, deliveryLng, customerLat, customerLng)
      : null;

  const distLabel =
    distance === null ? null :
    distance < 1 ? `${Math.round(distance * 1000)} m` :
    `${distance.toFixed(1)} km`;

  const etaMins = distance !== null ? Math.ceil((distance / 20) * 60) : null;
  const etaLabel =
    etaMins === null ? null :
    etaMins <= 1 ? 'Almost here!' :
    etaMins < 60 ? `~${etaMins} min` :
    `~${Math.ceil(etaMins / 60)} hr`;

  const isClose = distance !== null && distance <= 0.5;
  const isNear = distance !== null && distance > 0.5 && distance <= 2;
  const routeColor = isClose ? '#16a34a' : isNear ? '#d97706' : '#f97316';

  const compute = useCallback(() => {
    if (!customerLat || !customerLng) return;

    const lats = [customerLat];
    const lngs = [customerLng];
    if (deliveryLat && deliveryLng) {
      lats.push(deliveryLat);
      lngs.push(deliveryLng);
    }

    const bounds = {
      minLat: Math.min(...lats) - 0.007,
      maxLat: Math.max(...lats) + 0.007,
      minLng: Math.min(...lngs) - 0.007,
      maxLng: Math.max(...lngs) + 0.007,
    };

    // Customer pin always at bottom-right area (home)
    const cp = toXY(customerLat, customerLng, bounds, W, H, PAD);
    setCustPos(cp);

    if (deliveryLat && deliveryLng) {
      const dp = toXY(deliveryLat, deliveryLng, bounds, W, H, PAD);

      if (targetBike.current) {
        const prev = targetBike.current;
        if (Math.hypot(prev.x - dp.x, prev.y - dp.y) > 0.5) {
          prevBike.current = curBike.current || dp;
          targetBike.current = dp;
          animProg.current = 0;
          setPulsing(true);
          setTimeout(() => setPulsing(false), 900);
        }
      } else {
        prevBike.current = dp;
        targetBike.current = dp;
        curBike.current = dp;
        setBikePos(dp);
      }

      // Bearing: delivery boy faces toward customer
      setBearing(calcBearing(deliveryLat, deliveryLng, customerLat, customerLng));
    }
  }, [deliveryLat, deliveryLng, customerLat, customerLng]);

  // Animation loop
  useEffect(() => {
    let frame: number;
    const tick = () => {
      if (prevBike.current && targetBike.current && animProg.current < 1) {
        animProg.current = Math.min(animProg.current + 0.025, 1);
        const t = animProg.current;
        const e = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        const np = {
          x: lerp(prevBike.current.x, targetBike.current.x, e),
          y: lerp(prevBike.current.y, targetBike.current.y, e),
        };
        curBike.current = np;
        setBikePos(np);
      }
      dotAnimRef.current = (dotAnimRef.current + 0.35) % 20;
      setDotOffset(dotAnimRef.current);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    compute();
  }, [compute]);

  // Curved road path (quadratic bezier)
  const routePath = bikePos && custPos
    ? (() => {
        const mx = (bikePos.x + custPos.x) / 2 + (custPos.y - bikePos.y) * 0.28;
        const my = (bikePos.y + custPos.y) / 2 - (custPos.x - bikePos.x) * 0.28;
        return { path: `M ${bikePos.x} ${bikePos.y} Q ${mx} ${my} ${custPos.x} ${custPos.y}`, mx, my };
      })()
    : null;

  return (
    <div className="w-full bg-white rounded-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className={`px-4 py-2.5 border-b flex items-center justify-between ${isLive ? 'bg-orange-50 border-orange-100' : 'bg-gray-50 border-gray-100'}`}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-gray-900 flex items-center justify-center">
            <i className="ri-e-bike-2-fill text-white" style={{ fontSize: 11 }}></i>
          </div>
          <p className="text-gray-700 font-bold text-xs">ClothyBazar Live Delivery</p>
        </div>
        {isLive ? (
          <span className="flex items-center gap-1.5 text-xs text-orange-600 font-bold bg-orange-100 border border-orange-200 px-2.5 py-1 rounded-full">
            <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse inline-block"></span>
            LIVE
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs text-amber-500 font-medium">
            <i className="ri-loader-4-line animate-spin text-xs"></i>
            Rider GPS connecting...
          </span>
        )}
      </div>

      {/* Map canvas */}
      <div
        className="relative overflow-hidden"
        style={{
          height: 260,
          background: 'linear-gradient(135deg, #fafafa 0%, #f1f5f9 60%, #e8f5e9 100%)',
        }}
      >
        {/* Street grid */}
        <svg className="absolute inset-0 w-full h-full opacity-25" style={{ pointerEvents: 'none' }}>
          <defs>
            <pattern id="cstreets" width="48" height="48" patternUnits="userSpaceOnUse">
              <rect width="48" height="48" fill="none" />
              <line x1="48" y1="0" x2="48" y2="48" stroke="#94a3b8" strokeWidth="0.6" />
              <line x1="0" y1="48" x2="48" y2="48" stroke="#94a3b8" strokeWidth="0.6" />
            </pattern>
            <pattern id="cblocks" width="48" height="48" patternUnits="userSpaceOnUse">
              <rect x="2" y="2" width="43" height="43" rx="4" fill="#cbd5e1" fillOpacity="0.35" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#cblocks)" />
          <rect width="100%" height="100%" fill="url(#cstreets)" />
        </svg>

        {/* Main SVG */}
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" className="relative z-10">
          <defs>
            <radialGradient id="cbikeGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#f97316" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="ccustGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#16a34a" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#16a34a" stopOpacity="0" />
            </radialGradient>
            <filter id="cshadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="2.5" floodColor="#00000025" />
            </filter>
            <style>{`
              @keyframes cCustPing {
                0%, 100% { opacity: 0.9; r: 24; }
                50% { opacity: 0.15; r: 32; }
              }
              @keyframes cPulseOut {
                0% { transform: scale(1); opacity: 0.7; }
                100% { transform: scale(3); opacity: 0; }
              }
              @keyframes cBlink {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.15; }
              }
              @keyframes cSpinRing {
                to { transform: rotate(360deg); }
              }
              @keyframes cHouseScale {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.08); }
              }
            `}</style>
          </defs>

          {/* Road shadow */}
          {routePath && (
            <path d={routePath.path} fill="none" stroke="#00000012" strokeWidth="12" strokeLinecap="round" />
          )}

          {/* White road */}
          {routePath && (
            <path d={routePath.path} fill="none" stroke="white" strokeWidth="8" strokeLinecap="round" />
          )}

          {/* Animated dashed center line */}
          {routePath && (
            <path
              d={routePath.path}
              fill="none"
              stroke={routeColor}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray="9 6"
              strokeDashoffset={-dotOffset}
            />
          )}

          {/* Direction arrows along bezier curve */}
          {routePath && bikePos && custPos && (() => {
            const { mx, my } = routePath;
            return [0.28, 0.52, 0.75].map((t, i) => {
              const bx = (1 - t) ** 2 * bikePos.x + 2 * (1 - t) * t * mx + t ** 2 * custPos.x;
              const by = (1 - t) ** 2 * bikePos.y + 2 * (1 - t) * t * my + t ** 2 * custPos.y;
              const dt = 0.04;
              const t2 = Math.min(t + dt, 1);
              const bx2 = (1 - t2) ** 2 * bikePos.x + 2 * (1 - t2) * t2 * mx + t2 ** 2 * custPos.x;
              const by2 = (1 - t2) ** 2 * bikePos.y + 2 * (1 - t2) * t2 * my + t2 ** 2 * custPos.y;
              const angle = (Math.atan2(by2 - by, bx2 - bx) * 180) / Math.PI;
              return (
                <g key={i} transform={`translate(${bx}, ${by}) rotate(${angle})`}>
                  <polygon points="0,-3.5 6.5,0 0,3.5" fill={routeColor} fillOpacity="0.75" />
                </g>
              );
            });
          })()}

          {/* Customer home destination */}
          {custPos && (
            <g style={{ transformOrigin: `${custPos.x}px ${custPos.y}px`, animation: 'cHouseScale 2.5s ease-in-out infinite' }}>
              {/* Glow ring */}
              <circle cx={custPos.x} cy={custPos.y} r="28" fill="url(#ccustGlow)"
                style={{ transformOrigin: `${custPos.x}px ${custPos.y}px`, animation: 'cCustPing 2.2s ease-in-out infinite' }} />
              {/* Drop shadow */}
              <circle cx={custPos.x} cy={custPos.y + 1.5} r="16" fill="#00000018" />
              {/* Pin body */}
              <circle cx={custPos.x} cy={custPos.y} r="16" fill="#16a34a" filter="url(#cshadow)" />
              {/* White ring */}
              <circle cx={custPos.x} cy={custPos.y} r="11.5" fill="white" />
              {/* House icon */}
              <text x={custPos.x} y={custPos.y + 5} textAnchor="middle" fontSize="13" fill="#16a34a">
                🏠
              </text>
              {/* YOU label */}
              <rect x={custPos.x - 22} y={custPos.y + 19} width="44" height="15" rx="7" fill="#16a34a" />
              <text x={custPos.x} y={custPos.y + 30} textAnchor="middle" fontSize="7" fontWeight="bold" fill="white" fontFamily="system-ui">
                YOUR HOME
              </text>
            </g>
          )}

          {/* Delivery bike marker */}
          {bikePos && isLive && (
            <g>
              {/* Pulse on new location */}
              {pulsing && (
                <circle cx={bikePos.x} cy={bikePos.y} r="18" fill="none" stroke="#f97316" strokeWidth="2.5"
                  style={{ transformOrigin: `${bikePos.x}px ${bikePos.y}px`, animation: 'cPulseOut 0.9s ease-out forwards' }} />
              )}
              {/* Glow */}
              <circle cx={bikePos.x} cy={bikePos.y} r="22" fill="url(#cbikeGlow)" />
              {/* Direction arrow tip */}
              <polygon
                points={`${bikePos.x},${bikePos.y - 22} ${bikePos.x - 4.5},${bikePos.y - 15} ${bikePos.x + 4.5},${bikePos.y - 15}`}
                fill="#f97316"
                transform={`rotate(${bearing}, ${bikePos.x}, ${bikePos.y})`}
                style={{ transformOrigin: `${bikePos.x}px ${bikePos.y}px` }}
              />
              {/* Drop shadow */}
              <ellipse cx={bikePos.x} cy={bikePos.y + 17} rx="12" ry="3.5" fill="rgba(0,0,0,0.15)" />
              {/* Main circle */}
              <circle cx={bikePos.x} cy={bikePos.y} r="16" fill="#111827" stroke="white" strokeWidth="3" filter="url(#cshadow)" />
              {/* Bike emoji */}
              <text x={bikePos.x} y={bikePos.y + 6} textAnchor="middle" fontSize="16">
                🏍️
              </text>
              {/* Name tag */}
              <rect x={bikePos.x - 36} y={bikePos.y - 40} width="72" height="17" rx="6" fill="#111827" fillOpacity="0.93" />
              <text x={bikePos.x} y={bikePos.y - 27} textAnchor="middle" fontSize="7.5" fontWeight="bold" fill="white" fontFamily="system-ui">
                {deliveryBoyName.length > 14 ? deliveryBoyName.slice(0, 14) + '…' : deliveryBoyName}
              </text>
              {/* Live blink dot */}
              <circle cx={bikePos.x + 28} cy={bikePos.y - 32} r="3.5" fill="#f97316"
                style={{ animation: 'cBlink 0.9s ease-in-out infinite' }} />
            </g>
          )}

          {/* No live location — waiting state with animated bike */}
          {!isLive && custPos && (
            <g>
              {/* Waiting orbit ring */}
              <circle cx={W / 2} cy={H - 36} r="18" fill="none" stroke="#e2e8f0" strokeWidth="3" />
              <circle cx={W / 2} cy={H - 36} r="18" fill="none" stroke="#f97316" strokeWidth="3"
                strokeDasharray="24 28" strokeLinecap="round"
                style={{ transformOrigin: `${W / 2}px ${H - 36}px`, animation: 'cSpinRing 1.3s linear infinite' }} />
              <text x={W / 2} y={H - 31} textAnchor="middle" fontSize="15">🏍️</text>
              {/* Waiting label */}
              <rect x={W / 2 - 44} y={H - 20} width="88" height="14" rx="7" fill="#111827" fillOpacity="0.82" />
              <text x={W / 2} y={H - 9} textAnchor="middle" fontSize="7" fontWeight="bold" fill="white" fontFamily="system-ui">
                RIDER GPS ON HOGA...
              </text>
            </g>
          )}
        </svg>

        {/* Distance badge overlay */}
        {distLabel && isLive && (
          <div className={`absolute top-3 right-3 px-3 py-2 rounded-xl text-xs font-bold border flex items-center gap-1.5 shadow-sm ${
            isClose ? 'bg-green-100 text-green-700 border-green-300' :
            isNear ? 'bg-amber-100 text-amber-700 border-amber-300' :
            'bg-orange-50 text-orange-700 border-orange-200'
          }`}>
            <i className={`${isClose ? 'ri-checkbox-circle-fill' : 'ri-e-bike-2-fill'} text-sm`}></i>
            {distLabel}
          </div>
        )}

        {/* Last updated time */}
        {lastUpdated && isLive && (
          <div className="absolute bottom-2 left-3 bg-black/40 text-white text-[10px] px-2 py-0.5 rounded-lg backdrop-blur-sm">
            Updated: {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
        )}
      </div>

      {/* Info bar */}
      <div className={`px-4 py-3 border-t flex items-center justify-between ${
        isLive ? 'bg-white' : 'bg-orange-50 border-orange-100'
      }`}>
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isLive ? 'bg-orange-500' : 'bg-orange-100'}`}>
            <i className={`ri-e-bike-2-fill text-sm ${isLive ? 'text-white' : 'text-orange-500'}`}></i>
          </div>
          <div>
            <p className={`font-bold text-xs leading-none ${isLive ? 'text-gray-900' : 'text-orange-800'}`}>
              {isLive ? deliveryBoyName : 'Delivery partner'}
            </p>
            {isLive ? (
              <div className="flex items-center gap-1 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse inline-block"></span>
                <p className="text-orange-500 text-[10px] font-semibold">RASTE MEIN HAI</p>
              </div>
            ) : (
              <p className="text-orange-600 text-[10px] mt-0.5">GPS connect ho raha hai...</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2.5 text-right">
          {distLabel && isLive && (
            <div>
              <p className={`text-sm font-bold leading-none ${isClose ? 'text-green-600' : isNear ? 'text-amber-600' : 'text-orange-600'}`}>
                {distLabel}
              </p>
              <p className="text-gray-400 text-[10px] mt-0.5">Door</p>
            </div>
          )}
          {etaLabel && isLive && (
            <div className={`px-2.5 py-1.5 rounded-xl text-xs font-bold border ${
              isClose ? 'bg-green-100 text-green-700 border-green-200' :
              'bg-orange-100 text-orange-700 border-orange-200'
            }`}>
              <div className="flex items-center gap-1">
                <i className="ri-time-line text-xs"></i>
                {etaLabel}
              </div>
            </div>
          )}
          {!isLive && (
            <span className="flex items-center gap-1 text-xs text-amber-600 font-medium bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded-xl">
              <i className="ri-loader-4-line animate-spin text-xs"></i>
              Waiting...
            </span>
          )}
        </div>
      </div>

      {/* Address + progress bar */}
      {customerAddress && (
        <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-start gap-2">
          <i className="ri-home-4-fill text-green-500 text-xs mt-0.5 shrink-0"></i>
          <p className="text-gray-500 text-xs leading-relaxed line-clamp-1 flex-1">{customerAddress}</p>
          {isClose && (
            <span className="shrink-0 px-2 py-0.5 rounded-full bg-green-100 text-green-600 text-[10px] font-bold border border-green-200">
              Almost Here!
            </span>
          )}
        </div>
      )}

      {/* Progress strip */}
      {isLive && distance !== null && (
        (() => {
          const maxDist = 10;
          const pct = Math.max(5, Math.min(100, Math.round((1 - distance / maxDist) * 100)));
          return (
            <div className="px-4 py-2.5 border-t border-gray-100 bg-white">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-gray-500 font-medium">Delivery Progress</span>
                <span className={`text-[10px] font-bold ${isClose ? 'text-green-600' : 'text-orange-500'}`}>{pct}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${isClose ? 'bg-green-400' : 'bg-orange-400'}`}
                  style={{ width: `${pct}%` }}
                ></div>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[9px] text-gray-400">Warehouse</span>
                <span className="text-[9px] text-gray-400">Your Home</span>
              </div>
            </div>
          );
        })()
      )}
    </div>
  );
}
